import { inject, Injectable, isDevMode, NgZone } from '@angular/core';
import { MitIdCtaServiceProvider, PensionInfoUrlResponse } from '@cassiopeia/integration';

import { IntegrationApi } from '../../danish-pension';

import { ErrorWithMessageKey } from '../../custom-error/error-with-message-key';
import { EnvironmentService } from '../../environment';
import {
  PensionsInfoMessage,
  PensionsInfoSsoResult,
  PensionsInfoSsoType,
  SsoTransferPopupConfig,
} from './sso-transfer.model';

interface PopupDimensions {
  width: number;
  height: number;
  left: number;
  top: number;
}

interface PopupConfig extends SsoTransferPopupConfig {
  language: 'da' | 'en';
  width: number;
  height: number;
  timeout: number;
  ssoType: PensionsInfoSsoType;
}
const popupWindowName = 'SsoTransfer';

type SubmitType = 'form' | 'url';

@Injectable({ providedIn: 'root' })
export class SsoTransferService {
  readonly #api = inject(IntegrationApi).api.extendedApi.auth;
  readonly #ngZone = inject(NgZone);
  readonly #environment = inject(EnvironmentService);

  #popupWindow: Window | null = null;

  async openPensionInfoSSOPopup(config: SsoTransferPopupConfig = {}): Promise<PensionsInfoSsoResult> {
    return this.#openSSOPopup(config, 'pensionsinfo', 'form');
  }

  async openVicordaSSOPopup(config: SsoTransferPopupConfig = {}): Promise<PensionsInfoSsoResult> {
    return this.#openSSOPopup(config, 'vicorda', 'url');
  }

  /**
   * Opens a 3rd-party service in a pop-up window via MitID controlled transfer
   * and returns a Promise that resolves when the user finishes the session
   *
   * @param config Optional configuration for the pop-up window
   * @returns Promise that resolves with the result
   */
  async #openSSOPopup(
    config: SsoTransferPopupConfig = {},
    serviceProvider: MitIdCtaServiceProvider,
    submitType: SubmitType,
  ): Promise<PensionsInfoSsoResult> {
    if (isDevMode()) {
      console.warn(
        "Seems as if you are in dev mode. You can create test report data for this member by runnning the PensionsInfoV09TestDataGeneratorTask task in Polaris with the member's SSN number.",
      );
    }

    if (this.#popupWindow && !this.#popupWindow.closed) throw new ErrorWithMessageKey('popupAlreadyOpen');

    const closePopup = (): void => {
      if (this.#popupWindow && !this.#popupWindow.closed) {
        this.#popupWindow.close();
      }
      this.#popupWindow = null;
    };

    const popupConfig = this.#normalizeConfig(config);
    const dimensions = this.#calculatePopupDimensions(popupConfig.width, popupConfig.height);
    this.#openPopupWindow(
      this.#environment.environment.loadingUrl ?? '',
      dimensions,
      popupConfig.width,
      popupConfig.height,
    );
    const startTime = Date.now();
    try {
      const sessionTransfer = await this.#api.mitIdSessionTransfer(serviceProvider);
      const res = await this.#handleSessionTransferSuccess(
        sessionTransfer.data,
        popupConfig,
        submitType,
        serviceProvider,
      );
      closePopup();
      return res;
    } catch (err) {
      const duration = Date.now() - startTime;

      await new Promise<void>(resolve =>
        setTimeout(
          () => {
            closePopup();
            resolve();
          },
          Math.max(1000 - duration, 0),
        ),
      );

      throw err;
    }
  }

  /**
   * Normalizes and provides default values for popup configuration
   */
  #normalizeConfig(config: SsoTransferPopupConfig): PopupConfig {
    return {
      language: config.language ?? 'da',
      width: config.width ?? 1024,
      height: config.height ?? 768,
      timeout: config.timeout ?? 300000, // 5 minutes
      ssoType: config.ssoType ?? 3, // 3 = Send for self-service (default)
    };
  }

  /**
   * Handles successful session transfer URL retrieval from backend
   */
  async #handleSessionTransferSuccess(
    data: PensionInfoUrlResponse,
    config: PopupConfig,
    submitType: SubmitType,
    serviceProvider: MitIdCtaServiceProvider,
  ): Promise<PensionsInfoSsoResult> {
    const { exchangeCode, transferTokenText, ticket } = data;

    return new Promise<PensionsInfoSsoResult>((resolve, reject) => {
      if (!this.#popupWindow) {
        console.error('Failed to open pop-up window. Please check if pop-ups are blocked');
        reject({ errorKey: 'failedToOpenPopupWindow' });
        return;
      }

      let transferMessage: PensionsInfoSsoResult | undefined;
      let messageListener: ((event: MessageEvent) => void) | undefined;

      const onWindowClosed = () => {
        clearInterval(checkInterval);
        if (messageListener) {
          window.removeEventListener('message', messageListener);
        }
        this.#ngZone.run(() => {
          if (serviceProvider === 'pensionsinfo') {
            if (transferMessage?.success) {
              resolve(transferMessage);
            } else {
              reject(new ErrorWithMessageKey('pensionInfoTransferFailed'));
            }
          } else {
            resolve({ success: true, type: 'ClosePopUp' });
          }
        });
      };

      if (serviceProvider === 'pensionsinfo') {
        messageListener = (event: MessageEvent) => {
          if (!data.baseUrl.startsWith(event.origin)) {
            return;
          }
          const result = this.#parseTransferMessage(event.data as PensionsInfoMessage);
          console.debug('SSO transfer message received', { message: event.data, result });
          if (result?.type === 'ClosePopUp') {
            onWindowClosed();
          } else {
            transferMessage = result;
          }
        };
        window.addEventListener('message', messageListener);
      }

      const checkInterval = setInterval(() => {
        if (this.#popupWindow?.closed) {
          onWindowClosed();
        }
      }, 500);

      if (submitType === 'form') {
        const form = this.#createTransferForm(data.baseUrl, exchangeCode, transferTokenText, ticket, config.language);

        this.#submitFormToPopup(form);
      } else {
        this.#popupWindow.location.href = data.fullUrl;
      }
    });
  }

  /**
   * Calculates centered popup window dimensions
   */
  #calculatePopupDimensions(width: number, height: number): PopupDimensions {
    return {
      width,
      height,
      left: window.screen.width / 2 - width / 2,
      top: window.screen.height / 2 - height / 2,
    };
  }

  /**
   * Creates a hidden form with the required fields for session transfer
   */
  #createTransferForm(
    baseUrl: string,
    exchangeCode: string,
    transferTokenText: string,
    ticket: string,
    language: string,
  ): HTMLFormElement {
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = baseUrl;
    form.target = popupWindowName;
    form.style.display = 'none';

    const fields: Record<string, string> = {
      ct_exchangeCode: exchangeCode,
      ct_transferTokenText: transferTokenText,
      ticket: ticket,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    if (language === 'en') {
      const langInput = document.createElement('input');
      langInput.type = 'hidden';
      langInput.name = 'lang';
      langInput.value = 'en';
      form.appendChild(langInput);
    }

    return form;
  }

  /**
   * Opens the popup window outside of Angular's zone for performance
   */
  #openPopupWindow(url: string | URL, dimensions: PopupDimensions, width: number, height: number): void {
    const windowFeatures = `width=${width},height=${height},left=${dimensions.left},top=${dimensions.top},resizable=yes,scrollbars=yes,status=yes`;

    this.#ngZone.runOutsideAngular(() => {
      this.#popupWindow = window.open(url, popupWindowName, windowFeatures);
    });

    if (this.#popupWindow) {
      this.#popupWindow.focus();
    }
  }

  /**
   * Submits the form to the popup window and removes it from DOM
   */
  #submitFormToPopup(form: HTMLFormElement): void {
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  /**
   * Parses incoming messages from the SSO pop-up
   * Note: Actions are compared case-insensitively per PensionsInfo docs
   */
  #parseTransferMessage(data: PensionsInfoMessage): PensionsInfoSsoResult | undefined {
    const action = data?.action?.toLowerCase();

    switch (action) {
      case 'reportsentwithsuccess':
        return {
          success: true,
          type: 'ReportSentWithSuccess',
          data,
        };

      case 'reportsentwithfailure':
        return {
          success: false,
          type: 'ReportSentWithFailure',
          error: 'Failed to send report',
          data,
        };

      case 'nodatadelivered':
        return {
          success: true,
          type: 'NoDataDelivered',
          data,
        };

      case 'closepopup':
        return {
          success: false,
          type: 'ClosePopUp',
          error: 'User cancelled the session',
        };

      default:
        console.warn('[SSO Transfer] Unknown action:', data?.action, data);

        return;
    }
  }
}
